import { useEffect, useMemo, useRef, useState } from 'react';
import { projectTypeOptions } from '@command-center/admin-ecorp';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, cn } from '@command-center/ui-system';
import { ArrowLeft, ArrowRight, Building2, Check, FileUp, FormInput, Loader2, Search, Sparkles } from 'lucide-react';
import { checkDuplicates, createProjectFromSitePlan, createRecord, extractIntake, getExtractionRunStatus, listRecords, uploadDocument, type FieldSuggestion, type PortalRecord, type SuggestionDecision } from '../api';
import { display, formatBytes, useToast } from '../lib';
import { decisionsFromState, ExtractionReview, safeDecisionIds, type DecisionState } from './ExtractionReview';

type Mode = 'choose' | 'manual' | 'siteplan';
type WizardStep = 'upload' | 'processing' | 'review' | 'match' | 'creating';
type ProcessingStage = 'Uploading' | 'Reading Site Plan' | 'Extracting Project Facts' | 'Preparing Review';

const processingStages: ProcessingStage[] = ['Uploading', 'Reading Site Plan', 'Extracting Project Facts', 'Preparing Review'];

function sleep(ms: number): Promise<void> { return new Promise(resolve => window.setTimeout(resolve, ms)); }

/** Poll a just-started extraction run until it lands on completed/failed. */
async function pollExtractionRun(runId: string): Promise<Awaited<ReturnType<typeof getExtractionRunStatus>>> {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const status = await getExtractionRunStatus(runId);
    if (status.status === 'completed') return status;
    if (status.status === 'failed') throw new Error(status.error || 'Extraction failed.');
    await sleep(3000);
  }
  throw new Error('Extraction is taking longer than expected. Check back from the Documents page shortly.');
}

/**
 * The single shared Project creation flow, used by the New Project button,
 * the Command Palette action, and Alice's /new-project slash command.
 */
export function NewProjectFlow({ open, onClose, onCreated, initialMode }: { open: boolean; onClose(): void; onCreated(projectId: string): void; initialMode?: 'manual' | 'siteplan' }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'choose');
  useEffect(() => { if (open) setMode(initialMode ?? 'choose'); }, [open, initialMode]);
  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
        {mode === 'choose' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <button className="group rounded-xl border p-6 text-left transition hover:border-primary/50 hover:shadow-md" onClick={() => setMode('manual')}>
              <FormInput className="mb-3 h-7 w-7 text-primary"/>
              <p className="font-semibold">Enter Project manually</p>
              <p className="mt-1 text-sm text-muted-foreground">A compact form for the essentials — name, client, address, type, and stage.</p>
            </button>
            <button className="group rounded-xl border p-6 text-left transition hover:border-primary/50 hover:shadow-md" onClick={() => setMode('siteplan')}>
              <Sparkles className="mb-3 h-7 w-7 text-primary"/>
              <p className="font-semibold">Start from a Site Plan</p>
              <p className="mt-1 text-sm text-muted-foreground">Upload a site plan, review the extracted facts, then create the project.</p>
            </button>
          </div>
        )}
        {mode === 'manual' && <ManualForm onBack={() => setMode('choose')} onCreated={onCreated} onClose={onClose}/>}
        {mode === 'siteplan' && <SitePlanWizard onBack={() => setMode('choose')} onCreated={onCreated} onClose={onClose}/>}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Client picker (search existing clients / create new)
// ---------------------------------------------------------------------------

function ClientPicker({ value, onChange, suggestedName }: { value?: { id?: string; name: string; createNew?: boolean }; onChange(next?: { id?: string; name: string; createNew?: boolean }): void; suggestedName?: string }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PortalRecord[]>([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    const text = query.trim();
    if (!text) { setOptions([]); return; }
    const timer = window.setTimeout(() => {
      void listRecords('clients', new URLSearchParams({ pageSize: '6', search: text })).then(page => setOptions(page.items)).catch(() => setOptions([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-primary"/>{value.name}{value.createNew && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">New client</span>}</span>
          <Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>Change</Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground"/>
            <Input className="pl-9" placeholder="Search existing clients…" value={query} onChange={e => { setQuery(e.target.value); setOpenList(true); }} onFocus={() => setOpenList(true)}/>
          </div>
          {openList && (query.trim() || suggestedName) && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border bg-popover p-1 shadow-lg">
              {options.map(option => (
                <button key={option.id} type="button" className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { onChange({ id: option.id, name: display(option.fields['Client Name']) }); setOpenList(false); }}>
                  <span>{display(option.fields['Client Name'])}</span>
                  <span className="text-xs text-muted-foreground">{display(option.fields['Number of Projects'])} projects</span>
                </button>
              ))}
              {query.trim() && <button type="button" className="w-full rounded-md px-3 py-2 text-left text-sm text-primary hover:bg-muted" onClick={() => { onChange({ name: query.trim(), createNew: true }); setOpenList(false); }}>+ Create new client “{query.trim()}”</button>}
              {suggestedName && !query.trim() && <button type="button" className="w-full rounded-md px-3 py-2 text-left text-sm text-primary hover:bg-muted" onClick={() => { setQuery(suggestedName); }}>Search for “{suggestedName}” (from site plan)</button>}
              {!options.length && !query.trim() && !suggestedName && <p className="px-3 py-2 text-sm text-muted-foreground">Type to search clients.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Duplicate warnings
// ---------------------------------------------------------------------------

function DuplicateWarnings({ projects, clients }: { projects: PortalRecord[]; clients: PortalRecord[] }) {
  if (!projects.length && !clients.length) return null;
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
      <p className="font-medium text-amber-700 dark:text-amber-400">Possible duplicates found — double-check before creating.</p>
      {projects.length > 0 && <ul className="mt-1.5 space-y-0.5">{projects.map(item => <li key={item.id} className="text-muted-foreground">Project: <span className="font-medium text-foreground">{display(item.fields['Project Name'])}</span>{display(item.fields['Primary Project Address']) && ` — ${display(item.fields['Primary Project Address'])}`}</li>)}</ul>}
      {clients.length > 0 && <ul className="mt-1.5 space-y-0.5">{clients.map(item => <li key={item.id} className="text-muted-foreground">Client: <span className="font-medium text-foreground">{display(item.fields['Client Name'])}</span></li>)}</ul>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form
// ---------------------------------------------------------------------------

function ManualForm({ onBack, onCreated, onClose }: { onBack(): void; onCreated(projectId: string): void; onClose(): void }) {
  const { toast } = useToast();
  const [fields, setFields] = useState<Record<string, string>>({});
  const [client, setClient] = useState<{ id?: string; name: string; createNew?: boolean }>();
  const [duplicates, setDuplicates] = useState<{ projects: PortalRecord[]; clients: PortalRecord[] }>({ projects: [], clients: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const set = (name: string, value: string) => setFields(current => ({ ...current, [name]: value }));

  useEffect(() => {
    const name = fields['Project Name']?.trim(); const address = fields['Primary Project Address']?.trim();
    if (!name && !address) { setDuplicates({ projects: [], clients: [] }); return; }
    const timer = window.setTimeout(() => { void checkDuplicates({ name, address }).then(setDuplicates).catch(() => undefined); }, 500);
    return () => window.clearTimeout(timer);
  }, [fields['Project Name'], fields['Primary Project Address']]);

  async function submit() {
    if (!fields['Project Name']?.trim()) { setError('A project name is required.'); return; }
    setBusy(true); setError(undefined);
    try {
      let clientId = client?.id;
      if (client?.createNew) { const created = await createRecord('clients', { 'Client Name': client.name }); clientId = created.item.id; }
      const payload: Record<string, unknown> = Object.fromEntries(Object.entries(fields).filter(([, value]) => value?.trim()));
      if (clientId) payload.Client = [clientId];
      const result = await createRecord('projects', payload);
      toast(`Project “${fields['Project Name']}” created.`);
      onClose(); onCreated(result.item.id);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Project creation failed.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="field-label sm:col-span-2">Project name *<input className="field-control" value={fields['Project Name'] ?? ''} onChange={e => set('Project Name', e.target.value)}/></label>
        <div className="field-label sm:col-span-2"><span>Client</span><ClientPicker value={client} onChange={setClient}/></div>
        <label className="field-label">Project code<input className="field-control" value={fields['Project Code'] ?? ''} onChange={e => set('Project Code', e.target.value)}/></label>
        <label className="field-label">Project type<select className="field-control" value={fields['Project Type'] ?? ''} onChange={e => set('Project Type', e.target.value)}><option value="">—</option>{projectTypeOptions.map(option => <option key={option}>{option}</option>)}</select></label>
        <label className="field-label sm:col-span-2">Primary address<input className="field-control" value={fields['Primary Project Address'] ?? ''} onChange={e => set('Primary Project Address', e.target.value)}/></label>
        <label className="field-label">Jurisdiction<input className="field-control" value={fields['Jurisdiction'] ?? ''} onChange={e => set('Jurisdiction', e.target.value)}/></label>
        <label className="field-label">Internal project manager<input className="field-control" value={fields['Internal Project Manager'] ?? ''} onChange={e => set('Internal Project Manager', e.target.value)}/></label>
        <label className="field-label sm:col-span-2">Project description<textarea className="field-control min-h-20" value={fields['Project Description'] ?? ''} onChange={e => set('Project Description', e.target.value)}/></label>
      </div>
      <DuplicateWarnings projects={duplicates.projects} clients={duplicates.clients}/>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={busy}><ArrowLeft className="mr-1.5 h-4 w-4"/>Back</Button>
        <Button onClick={() => void submit()} disabled={busy}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin"/> : <Check className="mr-1.5 h-4 w-4"/>}Create Project</Button>
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site-plan-first wizard
// ---------------------------------------------------------------------------

function SitePlanWizard({ onBack, onCreated, onClose }: { onBack(): void; onCreated(projectId: string): void; onClose(): void }) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<WizardStep>('upload');
  const [stage, setStage] = useState<ProcessingStage>('Uploading');
  const [file, setFile] = useState<File>();
  const [documentDate, setDocumentDate] = useState('');
  const [revision, setRevision] = useState('');
  const [assetId, setAssetId] = useState<string>();
  const [runId, setRunId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<FieldSuggestion[]>([]);
  const [decisions, setDecisions] = useState<DecisionState>({});
  const [summary, setSummary] = useState<{ missingInformation: string[]; conflicts: string[]; confidence: number } | null>(null);
  const [client, setClient] = useState<{ id?: string; name: string; createNew?: boolean }>();
  const [duplicates, setDuplicates] = useState<{ projects: PortalRecord[]; clients: PortalRecord[] }>({ projects: [], clients: [] });
  const [error, setError] = useState<string>();

  const suggestionValue = (fieldKey: string): unknown => {
    const row = suggestions.find(item => item.field_key === fieldKey);
    if (!row) return undefined;
    const decision = decisions[row.id];
    if (decision?.action === 'reject') return undefined;
    return decision?.action === 'edit' ? decision.value : row.extracted_value;
  };
  const extractedClientName = useMemo(() => { const row = suggestions.find(item => item.field_key === 'Applicant'); return row ? String(row.extracted_value ?? '') : undefined; }, [suggestions]);

  async function process() {
    if (!file) { setError('Choose a site plan first.'); return; }
    setError(undefined); setStep('processing'); setStage('Uploading');
    try {
      const uploaded = await uploadDocument(file, { documentDate: documentDate || undefined, revision: revision || undefined, documentType: 'Site Plan' });
      const intakeAssetId = uploaded.intake?.assetId;
      if (!intakeAssetId) throw new Error('The upload succeeded but the intake reference is missing.');
      setAssetId(intakeAssetId);
      setStage('Reading Site Plan');
      const queued = await extractIntake(intakeAssetId);
      setRunId(queued.runId);
      setStage('Extracting Project Facts');
      const result = await pollExtractionRun(queued.runId);
      setStage('Preparing Review');
      const resultSuggestions = result.suggestions ?? [];
      setSuggestions(resultSuggestions);
      setSummary({ missingInformation: result.normalized?.missingInformation ?? [], conflicts: result.normalized?.conflicts ?? [], confidence: result.normalized?.confidence ?? 0 });
      const preselect: DecisionState = {};
      for (const id of safeDecisionIds(resultSuggestions)) preselect[id] = { action: 'accept' };
      setDecisions(preselect);
      setStep('review');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Processing failed. The uploaded file is kept — you can retry.');
      setStep('upload');
    }
  }

  async function toMatch() {
    setStep('match');
    const name = String(suggestionValue('Project Name') ?? '');
    const address = String(suggestionValue('Primary Project Address') ?? '');
    const parcelNumbers = String(suggestionValue('Assessor’s Parcel Numbers') ?? '');
    void checkDuplicates({ name, address, parcelNumbers, clientName: extractedClientName }).then(setDuplicates).catch(() => undefined);
  }

  async function create() {
    if (!assetId) return;
    setStep('creating'); setError(undefined);
    try {
      const accepted = decisionsFromState(decisions);
      // Identity fields go on the project directly; everything else is applied
      // through the reviewed-suggestion pipeline for provenance.
      const projectName = String(suggestionValue('Project Name') ?? '') || file?.name.replace(/\.[^.]+$/, '') || 'New Project';
      const result = await createProjectFromSitePlan({
        assetId, runId,
        projectFields: { 'Project Name': projectName, 'Project Type': suggestionValue('Project Type') || undefined },
        clientRecordId: client?.id,
        newClient: client?.createNew ? { 'Client Name': client.name } : undefined,
        documentMeta: { documentDate: documentDate || undefined, revision: revision || undefined, documentType: 'Site Plan' },
        decisions: accepted.filter(decision => { const row = suggestions.find(item => item.id === decision.id); return row && row.field_key !== 'Project Name'; }),
      });
      toast(`Project “${projectName}” created from the site plan (${result.applied} fields applied).`);
      onClose(); onCreated(result.item.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Project creation failed. The upload and extraction are kept — retry from here.');
      setStep('match');
    }
  }

  const steps: Array<{ id: WizardStep | 'create'; label: string }> = [{ id: 'upload', label: 'Upload' }, { id: 'processing', label: 'Process' }, { id: 'review', label: 'Review' }, { id: 'match', label: 'Match' }, { id: 'creating', label: 'Create' }];
  const stepIndex = steps.findIndex(item => item.id === step);

  return (
    <div className="space-y-4">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs">
        {steps.map((item, index) => (
          <li key={item.id} className={cn('flex items-center gap-1.5 rounded-full border px-2.5 py-1', index === stepIndex ? 'border-primary/50 bg-primary/10 font-medium text-primary' : index < stepIndex ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground')}>
            {index < stepIndex ? <Check className="h-3 w-3"/> : null}{item.label}
          </li>
        ))}
      </ol>

      {step === 'upload' && (
        <div className="space-y-4">
          <button type="button" className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-muted-foreground transition hover:border-primary/50 hover:text-foreground" onClick={() => fileRef.current?.click()}>
            <FileUp className="h-8 w-8"/>
            {file ? <span className="font-medium text-foreground">{file.name} <span className="font-normal text-muted-foreground">({formatBytes(file.size)})</span></span> : <span>Choose the site plan (PDF, PNG, JPG, WEBP)</span>}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={e => setFile(e.target.files?.[0])}/>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="field-label"><span>Client (optional)</span><ClientPicker value={client} onChange={setClient}/></div>
            <label className="field-label">Document date (optional)<input className="field-control" type="date" value={documentDate} onChange={e => setDocumentDate(e.target.value)}/></label>
            <label className="field-label">Revision (optional)<input className="field-control" value={revision} onChange={e => setRevision(e.target.value)} placeholder="e.g. 2"/></label>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={onBack}><ArrowLeft className="mr-1.5 h-4 w-4"/>Back</Button>
            <Button disabled={!file} onClick={() => void process()}><Sparkles className="mr-1.5 h-4 w-4"/>Upload & process</Button>
          </DialogFooter>
        </div>
      )}

      {step === 'processing' && (
        <div className="space-y-3 p-6">
          {processingStages.map(label => {
            const index = processingStages.indexOf(label); const current = processingStages.indexOf(stage);
            return <div key={label} className={cn('flex items-center gap-2 text-sm', index < current ? 'text-emerald-600 dark:text-emerald-400' : index === current ? 'font-medium text-foreground' : 'text-muted-foreground/60')}>{index < current ? <Check className="h-4 w-4"/> : index === current ? <Loader2 className="h-4 w-4 animate-spin"/> : <span className="h-4 w-4 rounded-full border"/>}{label}</div>;
          })}
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-4">
          {summary && (
            <div className="rounded-xl border bg-card p-3 text-sm text-muted-foreground">
              Extraction confidence {(summary.confidence * 100).toFixed(0)}% · {summary.missingInformation.length} missing item{summary.missingInformation.length === 1 ? '' : 's'} · {summary.conflicts.length} conflict{summary.conflicts.length === 1 ? '' : 's'}. Safe high-confidence fields are pre-accepted — adjust anything before the project is created.
            </div>
          )}
          <ExtractionReview suggestions={suggestions} state={decisions} onState={setDecisions}/>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('upload')}><ArrowLeft className="mr-1.5 h-4 w-4"/>Back</Button>
            <Button onClick={() => void toMatch()}>Continue<ArrowRight className="ml-1.5 h-4 w-4"/></Button>
          </DialogFooter>
        </div>
      )}

      {(step === 'match' || step === 'creating') && (
        <div className="space-y-4">
          <div className="field-label"><span>Client</span><ClientPicker value={client} onChange={setClient} suggestedName={extractedClientName}/></div>
          {!client && <p className="text-sm text-muted-foreground">You can continue without a confirmed client and link one later.</p>}
          <DuplicateWarnings projects={duplicates.projects} clients={duplicates.clients}/>
          <div className="rounded-xl border bg-card p-3 text-sm">
            <p><span className="text-muted-foreground">Project name:</span> <span className="font-medium">{String(suggestionValue('Project Name') ?? file?.name.replace(/\.[^.]+$/, '') ?? 'New Project')}</span></p>
            <p className="mt-1 text-muted-foreground">{decisionsFromState(decisions).filter(decision => decision.action !== 'reject').length} accepted fields will be applied with provenance.</p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={step === 'creating'} onClick={() => setStep('review')}><ArrowLeft className="mr-1.5 h-4 w-4"/>Back</Button>
            <Button disabled={step === 'creating'} onClick={() => void create()}>{step === 'creating' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin"/> : <Check className="mr-1.5 h-4 w-4"/>}Create Project</Button>
          </DialogFooter>
        </div>
      )}
    </div>
  );
}
